import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  Heading,
  SearchInput,
  Text,
  tabularNumsClass,
  type DataTableColumn,
} from "@lendcord/concord";
import { useGetBorrowersQuery } from "@/features/creditor/api/creditor.api";
import type { Borrower, LoanStatus, RegistrationStatus } from "@/features/creditor/creditor.types";

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

const PAGE_SIZE = 5;

const REGISTRATION_LABEL_IDS: Record<RegistrationStatus, string> = {
  continue: "lender.creditor.registrationActionContinue",
  "verify-mobile": "lender.creditor.registrationActionVerifyMobile",
};

const LOAN_STATUS_LABEL_IDS: Record<LoanStatus, string> = {
  approved: "lender.creditor.loanStatusApproved",
  pending: "lender.creditor.loanStatusPending",
  committed: "lender.creditor.loanStatusCommitted",
};

const LOAN_STATUS_BADGE: Record<LoanStatus, "success" | "warning" | "info"> = {
  approved: "success",
  pending: "warning",
  committed: "info",
};

function handleAddNewBorrower() {
  // TODO: wire to the real add-borrower flow once it exists.
}

function handleRegistrationAction() {
  // TODO: wire to the real registration flow once it exists.
}

interface BorrowersListProps {
  onBack: () => void;
}

export function BorrowersList({ onBack }: BorrowersListProps) {
  const intl = useIntl();
  const { data, isLoading } = useGetBorrowersQuery();
  const borrowers = data?.items;
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const totalCount = borrowers?.length ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const startIndex = (page - 1) * PAGE_SIZE;
  const pageItems = borrowers?.slice(startIndex, startIndex + PAGE_SIZE) ?? [];
  const rangeStart = totalCount === 0 ? 0 : startIndex + 1;
  const rangeEnd = Math.min(startIndex + PAGE_SIZE, totalCount);

  const columns: DataTableColumn<Borrower>[] = [
    {
      id: "borrowerId",
      header: intl.formatMessage({ id: "lender.creditor.columnBorrowerId" }),
      cell: (b) => b.borrowerId,
      cellClassName: `whitespace-nowrap ${tabularNumsClass}`,
    },
    {
      id: "name",
      header: intl.formatMessage({ id: "lender.creditor.columnBorrowerName" }),
      cell: (b) => b.name,
      cellClassName: "whitespace-nowrap",
    },
    {
      id: "loanAmount",
      header: intl.formatMessage({ id: "lender.creditor.columnLoanAmount" }),
      cell: (b) => currencyFormatter.format(b.loanAmount),
      cellClassName: `whitespace-nowrap ${tabularNumsClass}`,
    },
    {
      id: "totalRepaid",
      header: intl.formatMessage({ id: "lender.creditor.columnTotalRepaid" }),
      cell: (b) => currencyFormatter.format(b.totalRepaid),
      cellClassName: `whitespace-nowrap ${tabularNumsClass}`,
    },
    {
      id: "totalDue",
      header: intl.formatMessage({ id: "lender.creditor.columnTotalDue" }),
      cell: (b) => currencyFormatter.format(b.totalDue),
      cellClassName: `whitespace-nowrap ${tabularNumsClass}`,
    },
    {
      id: "registrationStatus",
      header: intl.formatMessage({ id: "lender.creditor.columnRegistrationStatus" }),
      cell: (b) => (
        <button
          type="button"
          onClick={handleRegistrationAction}
          className="text-sm font-medium text-accent-600 hover:underline"
        >
          <FormattedMessage id={REGISTRATION_LABEL_IDS[b.registrationStatus]} />
        </button>
      ),
      cellClassName: "whitespace-nowrap",
    },
    {
      id: "contactNumber",
      header: intl.formatMessage({ id: "lender.creditor.columnRegistrationContactNumber" }),
      cell: (b) => b.contactNumber,
      cellClassName: `whitespace-nowrap ${tabularNumsClass}`,
    },
    {
      id: "loanStatus",
      header: intl.formatMessage({ id: "lender.creditor.columnLoanStatus" }),
      cell: (b) => (
        <Badge status={LOAN_STATUS_BADGE[b.loanStatus]}>
          <FormattedMessage id={LOAN_STATUS_LABEL_IDS[b.loanStatus]} />
        </Badge>
      ),
      cellClassName: "whitespace-nowrap",
    },
  ];

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            aria-label={intl.formatMessage({ id: "lender.creditor.backToOverviewLabel" })}
            className="text-xl text-text-muted hover:text-text"
          >
            ‹
          </button>
          <Heading level={2}>
            <FormattedMessage id="lender.creditor.borrowerListHeading" />
          </Heading>
          <Button type="button" variant="secondary" className="border-border text-text hover:bg-surface">
            <FormattedMessage id="lender.creditor.filterAllLabel" /> ⌄
          </Button>
        </div>
        <button
          type="button"
          onClick={handleAddNewBorrower}
          className="text-sm font-medium text-accent-600 hover:underline"
        >
          <FormattedMessage id="lender.creditor.addNewBorrowerButton" />
        </button>
      </div>

      <div className="mb-4 max-w-sm">
        <SearchInput
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onClear={() => setQuery("")}
          placeholder={intl.formatMessage({ id: "lender.creditor.searchBorrowersPlaceholder" })}
        />
      </div>

      <DataTable
        columns={columns}
        rows={pageItems}
        rowKey={(b) => b.borrowerId}
        loading={isLoading}
        titleColumnId="name"
        metaColumnId="loanStatus"
        emptyState={
          <EmptyState>
            <FormattedMessage id="lender.creditor.noMatchingRecordsMessage" />
          </EmptyState>
        }
      />

      <div className="mt-4 flex items-center justify-between">
        <Text size="sm" variant="muted">
          <FormattedMessage id="lender.creditor.rowsPerPageLabel" values={{ count: PAGE_SIZE }} />
        </Text>
        <div className="flex items-center gap-3">
          <Text size="sm" variant="muted">
            <FormattedMessage
              id="lender.creditor.paginationRangeLabel"
              values={{ start: rangeStart, end: rangeEnd, total: totalCount }}
            />
          </Text>
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            aria-label={intl.formatMessage({ id: "lender.shared.previousPageLabel" })}
            className="text-text-muted disabled:opacity-40"
          >
            ‹
          </button>
          <button
            type="button"
            disabled={page >= pageCount}
            onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
            aria-label={intl.formatMessage({ id: "lender.shared.nextPageLabel" })}
            className="text-text-muted disabled:opacity-40"
          >
            ›
          </button>
        </div>
      </div>
    </div>
  );
}
